import React, {useEffect} from 'react';
import {Autocomplete, Avatar, Box, Stack, SxProps, TextField, Typography} from '@mui/material';
import {Theme} from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import CheckIcon from '@mui/icons-material/Check';
import {Pallete} from '../../theme/bluebird';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {FixedSizeList as List, ListChildComponentProps} from 'react-window';

export interface Option {
  key: string;
  label: string;
  picture: string;
}

interface AutocompleteInputProps {
  options: Option[];
  selectedOption?: Option;
  showEmptyOption?: boolean;
  emptyOptionLabel?: string;
  loading?: boolean;
  /** Virtualize the list of options */
  virtualized?: boolean;
  /** Do not show the avatar before the options (for options that are not people) */
  hideAvatars?: boolean;
  onChange: (selectedOption: Option) => void;
  sx?: SxProps<Theme>;
  disabled?: boolean;
}

export const AutocompleteInput = ({emptyOptionLabel = '', ...props}: AutocompleteInputProps) => {
  const {t} = useTranslation();
  const hint = React.useRef('');
  const [inputValue, setInputValue] = React.useState('');
  const [selectedOption, setSelectedOption] = React.useState<Option>({
    key: '',
    label: '',
    picture: '',
  });

  const handleChange = (event: React.ChangeEvent<{}>, selectedOption: Option) => {
    if (selectedOption) {
      hint.current = '';
      setInputValue(selectedOption.label);
      setSelectedOption(selectedOption);
      props.onChange(selectedOption);
    }
  };

  // use memo to add the empty option to the list of options
  const options = React.useMemo(() => {
    if (props.showEmptyOption) {
      return [{key: '', label: emptyOptionLabel, picture: ''}, ...props.options];
    }
    return props.options;
  }, [props.options, props.showEmptyOption, emptyOptionLabel]);

  const textStyles = {
    height: '100%',
    padding: '0!important',

    color: Pallete.gray.base,
    WebkitTextFillColor: `${Pallete.gray.base} !important`,
    fontWeight: '500',
    fontSize: '14px',
    lineHeight: '24px',
  };

  const widthStyles = (size: string) => {
    return {
      width: `${size} !important`,
      minWidth: `${size} !important`,
      maxWidth: `${size} !important`,
    };
  };

  const item = (option: Option, params: any) => {
    return (
      <Stack
        direction={'row'}
        sx={{
          display: 'flex',
          height: '40px',
          width: '100%',
          minWidth: 0,
        }}
      >
        {option.key !== '' && !props.hideAvatars && (
          <Avatar
            sx={{
              height: '28px',
              width: '28px',
              my: '6px',
              marginLeft: '16px',
              marginRight: '12px',
            }}
            src={option.picture}
          />
        )}
        <div style={{width: '100%', minWidth: 0}}>
          <TextField
            value={option.label}
            {...params}
            disabled={params === null}
            variant="outlined"
            InputLabelProps={{children: null}}
            onChange={(e) => {
              const newValue = e.target.value;
              setInputValue(newValue);
              const matchingOption = props.options.find((option) =>
                option.label.startsWith(newValue)
              );
              if (newValue && matchingOption) {
                hint.current = matchingOption.label;
              } else {
                hint.current = '';
              }
            }}
            placeholder={emptyOptionLabel}
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
              py: 0,
              px: 0,
              ml: option.key === '' || props.hideAvatars ? '16px' : 0,

              alignItems: 'center',
              textAlign: 'left',

              '& .MuiInputBase-input': {
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },

              '& fieldset': {
                border: 'none',
                pointerEvents: params === null ? 'fill' : '',
                cursor: params === null ? 'pointer' : '',
              },

              '& .MuiFormControl-root, .MuiInputBase-root, .MuiInputBase-input, .Mui-disabled': {
                ...textStyles,
                ...widthStyles(
                  params === null
                    ? '100%'
                    : selectedOption.key === ''
                      ? 'calc(100% - 44px)'
                      : 'calc(100% - 28px)'
                ),
              },

              '& .MuiInputBase-root': {
                alignSelf: 'flex-start',
                ...widthStyles('100%'),
              },
              '& .MuiAutocomplete-endAdornment': {
                paddingRight: selectedOption.key === '' ? '16px' : '',
              },
            }}
          />
          {params !== null && (
            <Typography
              sx={{
                display: 'flex',
                flexDirection: 'row',
                position: 'absolute',
                py: '8px',
                paddingRight: 0,
                paddingLeft: '57px',
                top: '62px',
                left: option.key === '' ? '136.5px' : '176px',

                opacity: 0.5,

                ...textStyles,
                color: Pallete.blue.base,
              }}
            >
              <span style={{visibility: 'hidden', whiteSpace: 'pre'}}>
                {hint.current.slice(0, inputValue.length)}
              </span>
              <span style={{whiteSpace: 'pre'}}>
                {hint.current.slice(inputValue.length, hint.current.length)}
              </span>
            </Typography>
          )}
        </div>
        {params === null && (
          <Box
            sx={{
              height: '40px',
              width: '28px',
              display: 'flex',
            }}
          >
            {selectedOption.key === option.key && (
              <SvgIcon sx={{fontSize: 'small', margin: 'auto', mr: '9px'}}>
                <CheckIcon />
              </SvgIcon>
            )}
          </Box>
        )}
      </Stack>
    );
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: Option) => {
    return (
      <li
        {...props}
        key={option.key}
        style={{
          padding: 0,
        }}
      >
        {item(option, null)}
      </li>
    );
  };

  const renderInput = (params: any) => {
    return <Box>{item(selectedOption, params)}</Box>;
  };

  useEffect(() => {
    setInputValue(props.selectedOption?.label || '');
    setSelectedOption(props.selectedOption || {key: '', label: '', picture: ''});
  }, [props.selectedOption]);

  return (
    <Autocomplete
      disabled={props.disabled}
      disableClearable
      onKeyDown={(event) => {
        if (event.key === 'Tab') {
          if (hint.current) {
            setInputValue(hint.current);
            event.preventDefault();
          }
        }
      }}
      onBlur={() => {
        hint.current = '';
      }}
      disablePortal
      inputValue={inputValue}
      filterOptions={(displayOptions, state) => {
        return displayOptions.filter(
          (option) =>
            option.label.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim()) ||
            option.key.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim())
        );
      }}
      loading={props.loading}
      loadingText={t(translations.search_select_input.loading)}
      options={props.loading ? [] : options}
      ListboxComponent={props.virtualized ? ListboxComponent : undefined}
      value={selectedOption}
      noOptionsText={t(translations.search_select_input.no_options)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => {
        return option.key === value.key;
      }}
      onChange={handleChange}
      renderOption={renderOption}
      renderInput={renderInput}
      sx={{
        width: {xs: '160px', md: '234px'},
        backgroundColor: 'white',
        borderRadius: '5px',
        ...props.sx,
      }}
      componentsProps={{
        paper: {
          sx: {
            width: {xs: '320px', md: '234px'},
            transform:
              selectedOption.key === ''
                ? 'translate3d(-15px, 5px, 0px)'
                : 'translate3d(-56px, 5px, 0px)',
          },
        },
      }}
    />
  );
};

// --------------------------------------------------------------------------
// --------------------------------------------------------------------------
// --------------------------------------------------------------------------

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const {children, ...other} = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 40; // Adjust as needed

    return (
      <div ref={ref} {...other}>
        <List
          height={Math.min(8, itemCount) * itemSize}
          width="100%"
          itemSize={itemSize}
          itemCount={itemCount}
          itemData={itemData}
          overscanCount={3}
        >
          {renderRow}
        </List>
      </div>
    );
  }
);

function renderRow(props: ListChildComponentProps) {
  const {data, index, style} = props;
  const option = data[index];

  return React.cloneElement(option, {style: {...style, padding: 0}});
}
